import React, { useState, useEffect } from 'react';
import { FileSubmission, TeamSubmissionsResponse } from '../utils/types';
import { FileUploadService } from '../utils/fileUploadService';
import { useAuth } from '../contexts/AuthContext';
import SingleFileUpload from './SingleFileUpload';

interface TeamFileManagerProps {
  teamId: string;
  onError?: (error: string) => void;
  onSuccess?: (message: string) => void;
}

const TeamFileManager: React.FC<TeamFileManagerProps> = ({
  teamId,
  onError,
  onSuccess
}) => {
  const [submissions, setSubmissions] = useState<FileSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();

  // Get current files by type
  const teamPhoto = submissions.find(s => s.submission_type === 'photo') || null;
  const teamRecording = submissions.find(s => s.submission_type === 'video') || null;
  const teamSubmission = submissions.find(s => s.submission_type === 'document') || null;

  useEffect(() => {
    if (teamId && token) {
      loadSubmissions();
    }
  }, [teamId, token]);

  const loadSubmissions = async () => {
    if (!token) return;

    setLoading(true);
    try {
      const response: TeamSubmissionsResponse = await FileUploadService.getTeamSubmissions(teamId, token);
      setSubmissions(response.submissions);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load submissions';
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSuccess = (response: any, type: string) => {
    onSuccess?.(`✅ ${type} uploaded successfully!`);
    loadSubmissions(); // Refresh the submissions list
  };

  const handleUploadError = (error: string) => {
    onError?.(error);
  };

  const handleFileDeleted = () => {
    onSuccess?.('✅ File removed successfully!');
    loadSubmissions(); // Refresh the submissions list
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{ fontSize: '18px', marginBottom: '10px' }}>📁</div>
        <div>Loading team files...</div>
      </div>
    );
  }

  return (
    <div className="team-file-manager">
      <div style={{ 
        marginBottom: '30px',
        textAlign: 'center'
      }}>
        <h2 style={{ color: '#333', marginBottom: '10px' }}>📎 Team Submissions</h2>
        <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>
          Upload one file for each required submission type. You can replace files at any time.
        </p>
      </div>

      <div style={{ 
        display: 'grid', 
        gap: '25px',
        gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
        marginBottom: '20px'
      }}>
        {/* Team Photo Upload */}
        <SingleFileUpload
          teamId={teamId}
          submissionType="photo"
          title="Team Photo"
          description="Upload a photo of your team"
          icon="📸"
          acceptedTypes=".jpg,.jpeg,.png,.gif,.webp"
          currentFile={teamPhoto}
          onUploadComplete={(response) => handleUploadSuccess(response, 'Team Photo')}
          onUploadError={handleUploadError}
          onFileDeleted={handleFileDeleted}
        />

        {/* Team Recording Upload */}
        <SingleFileUpload
          teamId={teamId}
          submissionType="video"
          title="Team Recording"
          description="Upload your team's presentation video"
          icon="🎥"
          acceptedTypes=".mp4,.avi,.mov"
          currentFile={teamRecording}
          onUploadComplete={(response) => handleUploadSuccess(response, 'Team Recording')}
          onUploadError={handleUploadError}
          onFileDeleted={handleFileDeleted}
        />

        {/* Team Submission Upload */}
        <SingleFileUpload
          teamId={teamId}
          submissionType="document"
          title="Team Submission"
          description="Upload your final submission document"
          icon="📄"
          acceptedTypes=".pdf,.doc,.docx,.txt"
          currentFile={teamSubmission}
          onUploadComplete={(response) => handleUploadSuccess(response, 'Team Submission')}
          onUploadError={handleUploadError}
          onFileDeleted={handleFileDeleted}
        />
      </div>

      {/* Summary Section */}
      <div style={{
        backgroundColor: '#f8f9fa',
        border: '1px solid #e9ecef',
        borderRadius: '8px',
        padding: '20px',
        marginTop: '20px'
      }}>
        <h4 style={{ margin: '0 0 15px 0', color: '#495057' }}>📊 Submission Status</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '8px' }}>📸</span>
            <span style={{ color: teamPhoto ? '#28a745' : '#6c757d' }}>
              Team Photo: {teamPhoto ? '✅ Uploaded' : '⏳ Pending'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '8px' }}>🎥</span>
            <span style={{ color: teamRecording ? '#28a745' : '#6c757d' }}>
              Recording: {teamRecording ? '✅ Uploaded' : '⏳ Pending'}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '8px' }}>📄</span>
            <span style={{ color: teamSubmission ? '#28a745' : '#6c757d' }}>
              Submission: {teamSubmission ? '✅ Uploaded' : '⏳ Pending'}
            </span>
          </div>
        </div>
        
        {/* Overall completion status */}
        <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #dee2e6' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 'bold' }}>Overall Progress:</span>
            <span style={{
              backgroundColor: submissions.length === 3 ? '#28a745' : submissions.length > 0 ? '#ffc107' : '#6c757d',
              color: 'white',
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
              {submissions.length}/3 Complete
            </span>
          </div>
          {submissions.length === 3 && (
            <div style={{ 
              marginTop: '10px', 
              padding: '10px', 
              backgroundColor: '#d4edda', 
              borderRadius: '4px',
              fontSize: '14px',
              color: '#155724'
            }}>
              🎉 All submissions complete! Your team is ready for review.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamFileManager;
